import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from '../../../Flux/index.js'
import type { AdminSpinQuery } from '../../../Flux/Slice/AdminSpin/AdminSpinSlice.js'
import { adminSpinSlice, cacheKey } from '../../../Flux/Slice/AdminSpin/AdminSpinSlice.js'
import type { RefSplitOrder, SpinReferenceKind } from '../../../Repository/Admin/AdminSpinRepository.js'
import type { AlgorithmCompareResponse } from '../../../Generated/AlgorithmCompareResponse.ts'
import type { SpinCohesionResponse } from '../../../Generated/SpinCohesionResponse.ts'
import type { DriftResponse } from '../../../Generated/DriftResponse.ts'
import type { HighIdentitySingletonResponse } from '../../../Generated/HighIdentitySingletonResponse.ts'
import type { MembershipFlowResponse } from '../../../Generated/MembershipFlowResponse.ts'
import type { RefDrilldown } from '../../../Generated/RefDrilldown.ts'
import type { RunTransition } from '../../../Generated/RunTransition.ts'
import type { SpinDetail } from '../../../Generated/SpinDetail.ts'
import type { SpinLeaderboardResponse } from '../../../Generated/SpinLeaderboardResponse.ts'
import type { SpinMetricsResponse } from '../../../Generated/SpinMetricsResponse.ts'
import type { SpinReferenceResponse } from '../../../Generated/SpinReferenceResponse.ts'
import type { SpinRun } from '../../../Generated/SpinRun.ts'

/*
 * Every hook here reads through the shared admin-SPIN response cache (Flux/Slice/AdminSpin) rather than
 * holding its own useState. That is what makes the panel navigable: `/metrics` is mounted by four pages
 * and the whole-set queries take seconds, so without a cache every click paid for them again.
 *
 * Two behaviours carried over from the pre-cache version, because the pages depend on them:
 *  * A stale answer is never shown under a new question. The cache is keyed, so changing run / amplicon /
 *    limit selects a DIFFERENT entry — strictly safer than the old "clear on change", where the previous
 *    result stayed on screen until an effect cleared it.
 *  * The heavy endpoints stay opt-in: `optIn` hooks never fetch on mount, only via `load()`. What is new
 *    is that an already-cached result IS shown on mount, so returning to the page is instant.
 */

/**
 * The (algorithmType, asOf, amplicon) triple every current-set query is scoped by, read off the selected
 * run. Null when no run is selected yet — the signal not to fetch at all.
 */
const runScope = <K extends AdminSpinQuery['kind']>(
  kind: K,
  run: SpinRun | null
): { kind: K; algorithmType: string; asOf: string; amplicon: string | null } | null => {
  if (!run?.algorithmType || !run.startedAt) return null
  return { kind, algorithmType: run.algorithmType, asOf: run.startedAt, amplicon: run.amplicon ?? null }
}

interface QueryResult<T> {
  data: T | null
  loading: boolean
  error: string | null
  /** Force a re-fetch, bypassing the cache — what the pages' Load / Reload buttons call. */
  load: () => void
}

/**
 * Bind one query descriptor to the cache. A null [query] means "not answerable yet" (no run selected)
 * and never fetches.
 *
 * The effect keys on the derived cache key alone and reads the descriptor from a ref. That is exact
 * rather than a lint dodge: [cacheKey] is a total function of the descriptor, so two renders with the
 * same key necessarily carry an equivalent descriptor — while the descriptor object itself is rebuilt
 * every render and would re-fire the effect forever if used as a dependency.
 */
const useAdminSpinQuery = <T>(query: AdminSpinQuery | null, optIn = false): QueryResult<T> => {
  const dispatch = useDispatch()
  const key = query ? cacheKey(query) : null

  const queryRef = useRef(query)
  queryRef.current = query

  const entry = useSelector((state) => (key ? state.adminSpin.entries[key] : undefined))

  useEffect(() => {
    if (optIn || !queryRef.current) return
    dispatch(adminSpinSlice.actions.query({ query: queryRef.current }))
  }, [dispatch, key, optIn])

  const load = useCallback(() => {
    if (!queryRef.current) return
    dispatch(adminSpinSlice.actions.query({ query: queryRef.current, force: true }))
  }, [dispatch, key])

  return {
    data: (entry?.data as T | undefined) ?? null,
    loading: entry?.status === 'loading',
    error: entry?.status === 'error' ? (entry.error ?? 'Request failed') : null,
    load
  }
}

/** The list of clustering runs. Cached for the session — a new run landing mid-session is rare. */
export const useSpinRuns = () => {
  const { data, loading, error } = useAdminSpinQuery<SpinRun[]>({ kind: 'runs' })
  return { runs: data ?? [], loading: loading && data === null, error }
}

/**
 * Full-set cohesion (unity/reach over EVERY current SPIN) — OPT-IN: ~75s measured warm, so nothing
 * is fetched on mount and the caller must invoke `load()`. A previously loaded answer for the same run
 * IS served from cache on mount; `load()` forces a refresh.
 */
export const useSpinCohesion = (run: SpinRun | null) => {
  const { data, loading, error, load } = useAdminSpinQuery<SpinCohesionResponse>(runScope('cohesion', run), true)
  return { cohesion: data, loading, error, load }
}

/**
 * High-identity singletons (one member, near-perfect NCBI match) — OPT-IN, same contract as
 * [useSpinCohesion]. ~3.3s: singleton-ness can only be decided over the whole resolved current set.
 */
export const useSpinHighIdentitySingletons = (run: SpinRun | null, minHitPercentage?: number, limit?: number) => {
  const scope = runScope('singletons', run)
  const { data, loading, error, load } = useAdminSpinQuery<HighIdentitySingletonResponse>(
    scope ? { ...scope, minHitPercentage, limit } : null,
    true
  )
  return { singletons: data, loading, error, load }
}

/**
 * "Top SPINs" leaderboards — OPT-IN, same contract as [useSpinCohesion]. ~7.4s: a whole-set aggregation
 * over every current member, with no seed to start from.
 */
export const useSpinLeaderboard = (run: SpinRun | null, limit?: number) => {
  const scope = runScope('leaderboard', run)
  const { data, loading, error, load } = useAdminSpinQuery<SpinLeaderboardResponse>(
    scope ? { ...scope, limit } : null,
    true
  )
  return { leaderboard: data, loading, error, load }
}

/**
 * Per-SPIN metrics for the CURRENT set as of the selected run's timestamp (currency by assigned_at, not
 * run id). Mounted by four pages, which is why caching it matters most.
 */
export const useSpinMetrics = (run: SpinRun | null) => {
  const { data, loading, error } = useAdminSpinQuery<SpinMetricsResponse>(runScope('metrics', run))
  return { metrics: data, loading, error }
}

/** Per-run recompute outcomes for one algorithm. */
export const useSpinLineage = (algorithmType: string | undefined) => {
  const { data, loading, error } = useAdminSpinQuery<RunTransition[]>(
    algorithmType ? { kind: 'lineage', algorithmType } : null
  )
  return { lineage: data ?? [], loading, error }
}

/** OTU-membership flow for the selected run. */
export const useSpinMembershipFlow = (runId: string | undefined) => {
  const { data, loading, error } = useAdminSpinQuery<MembershipFlowResponse>(
    runId ? { kind: 'membershipFlow', runId } : null
  )
  return { flow: data, loading, error }
}

/**
 * Taxonomic drift for one run (its reminted clusters vs predecessors). `rank` undefined ⇒ all ranks;
 * `source` '' ⇒ all sources — both are part of the cache key, so each filter combination is its own entry.
 */
export const useSpinDrift = (runId: string | undefined, rank?: number, source?: string) => {
  const { data, loading, error } = useAdminSpinQuery<DriftResponse>(
    runId ? { kind: 'drift', runId, rank, source } : null
  )
  return { drift: data, loading, error }
}

/**
 * Reference concordance (NCBI id / NCBI taxon id / BOLD bin split-vs-kept) for the current set.
 *
 * `orderBy` / `minHitPercentage` only shape the `mostSplit` list (the KPIs and fan-out charts are
 * unfiltered). Both are optional and omitted from the request when undefined, so the backend defaults
 * apply (orderBy=SUSPICION, no identity floor). A floor DROPS reference ids with no readable identity —
 * SQL `NULL >= 99.5` is NULL — so the view must say so.
 */
export const useSpinReference = (
  run: SpinRun | null,
  refKind: SpinReferenceKind,
  orderBy?: RefSplitOrder,
  minHitPercentage?: number
) => {
  const scope = runScope('reference', run)
  const { data, loading, error } = useAdminSpinQuery<SpinReferenceResponse>(
    scope ? { ...scope, refKind, orderBy, minHitPercentage } : null
  )
  return { reference: data, loading, error }
}

/**
 * Drill-down for ONE reference id (which current SPINs it is split across).
 *
 * Fetch policy is NOT uniform across reference kinds. `NCBI_ID` / `BOLD_BIN` are sub-second, so they
 * fetch on mount like every other hook here. `NCBI_TAXID` seeds off an unindexed column (~4.4s warm /
 * ~40s cold per statement × 3 statements), so it is OPT-IN: nothing is fetched until `load()` is called.
 */
export const useSpinReferenceDetail = (
  run: SpinRun | null,
  refKind: SpinReferenceKind | null,
  refId: string,
  limit?: number
) => {
  const scope = runScope('referenceDetail', run)
  const { data, loading, error, load } = useAdminSpinQuery<RefDrilldown>(
    scope && refKind && refId ? { ...scope, refKind, refId, limit } : null,
    refKind === 'NCBI_TAXID'
  )
  return { detail: data, loading, error, load }
}

/** The A-vs-B algorithm comparison (summaries + concordance). */
export const useSpinCompare = (a: string | undefined, b: string | undefined, amplicon?: string | null) => {
  const { data, loading, error } = useAdminSpinQuery<AlgorithmCompareResponse>(
    a && b ? { kind: 'compare', a, b, amplicon: amplicon ?? null } : null
  )
  return { compare: data, loading, error }
}

/**
 * Full detail for one SPIN by id. `spin.*` rows are immutable (`forbid_mutation` trigger), so a cached
 * entry can never go stale. Reports `loading` until the first answer lands, matching the previous hook —
 * SpinDetailPage renders a spinner off it rather than an empty page.
 */
export const useSpinDetail = (spinId: string | undefined) => {
  const { data, loading, error } = useAdminSpinQuery<SpinDetail>(spinId ? { kind: 'detail', spinId } : null)
  return { detail: data, loading: loading || (spinId !== undefined && data === null && error === null), error }
}
